<template>
	<div>
		<span class="list-group">
			<setting-input
				:set-active="setEmailActive"
				:on-commit="changeEmail"
				:status="userStatusEmail"
				:on-reset="resetState"
				:require-password="true"
				:display-value="user.email"
				:label="'Email'"
				:input-ref="'email'"
				:button-text="'Change Email'"
				:can-edit="!!(roles.usr && roles.usr.info && roles.usr.info.update)">
				<!-- input slot -->
				<input slot="setting-input" class="wide" v-model="email" placeholder="New email" type="email"/>
				<!-- loading slot -->
				<spinner slot="updating-inline" :text="'Processing'" v-if="userStatusEmail.loading"></spinner>
				<!-- help text slot -->
				<small slot="help-text" class="field-assist">You will receive an email at the current email address to verify.</small>
				<!-- error slot -->
				<blockquote v-if="userStatusEmail.error && userStatusEmail.error.changeEmailTaken" slot="error-inline" class="notice error left gap">
						Email is taken. Try another.
					</blockquote>
				<blockquote v-else-if="userStatusEmail.error && userStatusEmail.error.changeEmailInvalid" slot="error-inline" class="notice error left gap">
					Invalid email. Try another.
				</blockquote>
				<!-- success slot -->
				<collapsible slot="success-block" :open="userStatusEmail.success">

					<blockquote class="notice info">
						An email has been sent to <em>{{newEmail}}</em>.
						<p>Click the link in the email to complete this change.</p>
						<p>This browser window/tab can be closed now or refreshed once the change has been confirmed.</p>
					</blockquote>

				</collapsible>

			</setting-input>

			<setting-input
				class="no-display"
				:on-commit="changePassword"
				:on-reset="resetState"
				:require-password="true"
				:label="'Password'"
				:button-text="'Change Password'"
				:can-edit="!!(roles.usr && roles.usr.password && roles.usr.password.update)"
				:status="userStatusPassword">
				<!-- input slot -->
				<input slot="setting-input" class="wide" v-model="newPassword" placeholder="New password" type="password"/>
				<!-- loading slot -->
				<spinner slot="updating-inline" :text="'Processing'" v-if="userStatusPassword.loading"></spinner>
				<!-- help text slot -->
				<small slot="help-text" class="field-assist">Passwords must be 8+ letters or numbers.</small>
				<!-- error slot -->
				<blockquote v-if="userStatusPassword.error && userStatusPassword.error.changePasswordInvalid" slot="error-inline" class="notice error left gap">
					Invalid password. Try something else.
				</blockquote>
				<!-- inline success slot -->
				<div v-if="userStatusPassword.success" slot="success-inline" class="notice success inline-block">
					<i class="fas fa-check"></i> Updated
				</div>
			</setting-input>


			<the-mfa-toggle
				:can-edit="!!(roles.access && roles.access.mfa && roles.access.mfa.create)"
				:on-commit="updateTfa"
				v-on:change="toggleTfa"
				v-on:reset="resetState"
				:error="userStatusEnableTfa.error || userStatusDisableTfa.error"
				:model="user.isMfaEnabled"
				:require-password="true"
				:min-height="500"
				:label="'Two-step authentication'">
				<!-- loading slot -->
				<spinner slot="updating-inline" :text="'Processing'" v-if="userStatusEnableTfa.loading || userStatusDisableTfa.loading"></spinner>
				<!-- toggle info slot -->
				<div slot="toggle-block" class="row">
					<spinner class="centered" v-if="!tfa.qr && userStatusCreateTfa.loading"></spinner>
					<span class="column gap" v-if="tfa.qr && !userStatusEnableTfa.success">
						<blockquote class="notice info">
							Install a two-step authentication app such as <a target="_blank" href="https://1password.com/downloads/">1Password</a> or <a target="_blank" href="https://support.google.com/accounts/answer/1066447?co=GENIE.Platform%3DiOS&hl=en&oco=0">Google Authenticator</a> on your smartphone or tablet.
							<p>Next, scan the QR code to receive a one-time passcode and enter it the first field below.</p>
							<p>Last, wait for a second code to appear, enter it and press 'Apply'.</p>
						</blockquote>
						<vue-img class="centered rigid" :data="tfa.qr"/>
						<blockquote class="notice error center" v-if="userStatusEnableTfa.error && userStatusEnableTfa.error.tfaNotEnabled">
							You need to scan the QR code before entering passcodes.
						</blockquote>
					</span>
				</div>
				<!-- error slots -->
				<blockquote v-if="userStatusEnableTfa.error && userStatusEnableTfa.error.tfaFirstCodeInvalid" slot="error-inline-footer" class="notice error gap right">
					Your first code is wrong.
				</blockquote>
				<blockquote v-else-if="userStatusEnableTfa.error && userStatusEnableTfa.error.tfaSecondCodeInvalid" slot="error-inline-footer" class="notice error gap right">
					Your second code is wrong.
				</blockquote>
				<blockquote v-else-if="userStatusDisableTfa.error && userStatusDisableTfa.error.authPasswordInvalid" slot="error-inline-footer" class="notice error gap right">
					It appears that this password was wrong.
				</blockquote>
				<!-- success slots -->
				<div v-if="userStatusEnableTfa && userStatusEnableTfa.success" slot="success-inline" class="notice success inline-block">
					<i class="fas fa-check"></i> Updated
				</div>
				<div v-if="userStatusDisableTfa && userStatusDisableTfa.success" slot="success-inline" class="notice success inline-block">
					<i class="fas fa-check"></i> Updated
				</div>
			</the-mfa-toggle>

			<close-account-partial v-if="!$root.isTek"></close-account-partial>
		</span>
	</div>
</template>

<script>
import SettingInput from '../components/SettingInput';
import CloseAccountPartial from '@/views/CloseAccount.partial';
import SettingPlaceholder from '../components/SettingPlaceholder';
import TheMfaToggle from '../components/TheMfaToggle';
import Collapsible from '../components/Collapsible';
import VueImg from '../components/VueImg';
import Spinner from '../components/Spinner';
import { mapGetters } from 'vuex';
export default {
	name: 'SecurityPartial',
	data() {
		return {
			newPassword: '',
			email: '',
			newEmail: '',
			enableFn: null
		};
	},
	computed: {
		...mapGetters([
			'user',
			'roles',
			'userStatus',
			'userStatusEmail',
			'userStatusPassword',
			'userStatusCreateTfa',
			'userStatusEnableTfa',
			'userStatusDisableTfa',
			'accountStatus',
			'tfa'
		]),
		tfaCreated() {
			return this.tfa && this.tfa.qr;
		},
		setEmailActive() {
			return this.$route.query && this.$route.query.active === 'email';
		},
	},
	components: {
		CloseAccountPartial,
		SettingInput,
		SettingPlaceholder,
		Spinner,
		TheMfaToggle,
		Collapsible,
		VueImg
	},
	methods: {
		createTfa() {
			this.$store.dispatch('CREATE_TFA_REQUEST');
		},
		toggleTfa(enabled) {
			this.$store.dispatch('RESET_USER_STATUS');
			this.tfaModel = enabled;
			this.enableFn = enabled ? this.enableTfa : this.disableTfa;
			if (!this.tfaCreated && !this.tfaEnabled) {
				this.createTfa();
			}
		},
		updateTfa(firstCode, secondCode) {
			return this.enableFn(firstCode, secondCode);
		},
		enableTfa(firstCode, secondCode) {
			return new Promise((resolve, reject) => {
				this.$store.dispatch('ENABLE_TFA_REQUEST', {
					firstCode: firstCode,
					secondCode: secondCode
				})
					.then(response => {
						resolve(response);
					})
					.catch(reason => {
						reject(reason);
					});
			});
		},
		disableTfa(password) {
			return new Promise((resolve, reject) => {
				this.$store.dispatch('DISABLE_TFA_REQUEST', {
					password: password
				})
					.then(response => {
						resolve(response);
					})
					.catch(reason => {
						reject(reason);
					});
			});
		},
		changeEmail(password) {
			let vm = this;
			return new Promise((resolve, reject) => {
				this.$store.dispatch('EMAIL_CHANGE_REQUEST', {
					email: vm.email,
					password: password
				})
					.then(response => {
						vm.newEmail = vm.email;
						vm.email = '';
						resolve(response);
					})
					.catch(reason => {
						reject(reason);
					});
			});
		},
		changePassword(password) {
			let vm = this;
			return new Promise((resolve, reject) => {
				this.$store.dispatch('PASSWORD_CHANGE_REQUEST', {
					newPassword: vm.newPassword,
					password: password
				})
					.then(response => {
						vm.newPassword = '';
						resolve(response);
					})
					.catch(reason => {
						reject(reason);
					});
			});
		},
		resetState() {
			this.$store.dispatch('RESET_USER_STATUS');
		}
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
	.tfa-code {
		width: 75px;
	}
	.notice.error.center::before,
	.notice.error.center::after {
	    left: 50%;
	    margin-left: -14px;
	}
	.blockquote .info.notice {
		/* margin-bottom: 1em; */
	}
	img {
		border: 1px solid #eee;
		margin-top: 1em;
		border-radius: 3px;
		width: max-content;
	}

</style>



// WEBPACK FOOTER //
// ./src/views/Security.partial.vue